import type { ReactElement } from 'react';
import { Route } from 'react-router';
import Maybe from 'true-myth/maybe';

import type { FeatureFlagService } from '~/shared/feature-flag-service/feature-flag-service';
import type { Translate } from '~/shared/internationalization/translation';
import { PageWithTitle } from '~/shared/page-with-title/PageWithTitle';

import { PageWithFeatureFlagEnabled } from './PageWithFeatureFlagEnabled';
import { PageWithPageViewTracking } from './PageWithPageViewTracking';
import type { RouteDefinition } from './route';

export type RoutesFromRouteDefinitionsDependencies = {
    readonly translate: Translate;
};

export type RoutesFromRouteDefinitionsOptions = {
    readonly featureFlagService: FeatureFlagService;
    readonly routeDefinitions: readonly RouteDefinition[];
};

export const createRoutesFromRouteDefinitions = (
    dependencies: RoutesFromRouteDefinitionsDependencies,
    options: RoutesFromRouteDefinitionsOptions,
    // biome-ignore lint/suspicious/noExplicitAny: suppressed due to migration
): readonly ReactElement<any>[] => {
    const { translate } = dependencies;
    const { featureFlagService, routeDefinitions } = options;

    return routeDefinitions.map((routeDefinition) => {
        const { documentTitle, page: RoutePage, pageProperties, path } = routeDefinition;
        const behindFeatureName = Maybe.of(routeDefinition.behindFeatureName);
        const title = translate(documentTitle);

        const element = behindFeatureName.match({
            Just(behindFeatureNameValue) {
                return (
                    <PageWithPageViewTracking>
                        <PageWithFeatureFlagEnabled
                            enabledFeatureName={behindFeatureNameValue}
                            featureFlagService={featureFlagService}
                        >
                            <PageWithTitle title={title}>
                                {}
                                <RoutePage {...pageProperties} />
                            </PageWithTitle>
                        </PageWithFeatureFlagEnabled>
                    </PageWithPageViewTracking>
                );
            },
            Nothing() {
                return (
                    <PageWithPageViewTracking>
                        <PageWithTitle title={title}>
                            {}
                            <RoutePage {...pageProperties} />
                        </PageWithTitle>
                    </PageWithPageViewTracking>
                );
            },
        });

        return <Route key={path} element={element} path={path} />;
    });
};
